import { Controller } from "@hotwired/stimulus";
import { Scanner, QrCode, Barcode } from "../plugins/scanner";

// Connects to data-controller="scanner"
export default class extends Controller {
  static values = {
    readerId: String,
    qrPrefix: String,
    barPrefix: String,
    barLength: Number,
    sequence: Boolean,
  };

  static targets = [
    "qrInstructions",
    "barInstructions",
    "feedback",
    "message",
    "form",
    "barcode",
    "crateNumber",
    "manualBtn",
  ];

  connect() {
    const codes = {};

    if (this.qrPrefixValue) {
      codes.qr = new QrCode({ prefix: this.qrPrefixValue });
    }

    if (this.barPrefixValue && this.barLengthValue) {
      codes.bar = new Barcode({
        prefix: this.barPrefixValue,
        length: this.barLengthValue,
      });
    }

    this.scanner = new Scanner(this.readerIdValue, codes);

    // Keep a cache to avoid sending 2x the same crate
    this.previousQrCode = null;

    this.scan();
  }

  disconnect() {
    this.previousQrCode = null;
    this.stop().then(() => (this.scanner = null));
  }

  quit(e) {
    e.preventDefault();
    this.stop().then(() => Turbo.visit(e.target.closest("a").href));
  }

  scan() {
    if (this.sequenceValue) {
      this.scanSequence();
    } else {
      this.scanQrOnly();
    }
  }

  scanSequence() {
    this.#scanQrCode(() => {
      this.#scanBarcode(() => {
        this.#submitAndRestart();
      });
    });
  }

  scanQrOnly() {
    this.#scanQrCode(
      () => {
        if (this.crateNumberTarget.value !== this.previousQrCode) {
          this.previousQrCode = this.crateNumberTarget.value;
          this.#submitAndRestart();
        }
      },
      { skipDoubles: true },
    );
  }

  async stop() {
    await this.scanner.stop();
  }

  async toggleTorch() {
    await this.scanner.toggleTorch();
  }

  resetForm() {
    if (this.hasFormTarget) {
      this.formTarget.reset();
    }
  }

  #submitForm() {
    if (this.hasFormTarget) {
      this.formTarget.requestSubmit();
      this.resetForm();
    }
  }

  #scanQrCode(nextStep = () => {}, { skipDoubles = false } = {}) {
    this.#showQrInstructions();

    this.scanner.scan(
      this.scanner.qr,
      async (scannedString) => {
        this.#setSuccess(scannedString);

        const regex = new RegExp(`^${this.qrPrefixValue}`, "i");
        this.crateNumberTarget.value = scannedString.replace(regex, "");

        if (
          !(skipDoubles && this.crateNumberTarget.value === this.previousQrCode)
        ) {
          this.#setManualUrlParam();

          this.#hideQrInstructions();
          await this.stop();
        }

        nextStep();
      },
      (decodedResult) => {
        console.debug("invalid qr code:", decodedResult);
      },
    );
  }

  #scanBarcode(nextStep = () => {}) {
    this.#showBarInstructions();
    this.scanner.scan(
      this.scanner.bar,
      async (scannedString) => {
        this.barcodeTarget.value = scannedString;

        this.#hideBarInstructions();
        await this.stop();

        nextStep();
      },
      (decodedResult) => {
        console.debug("invalid barcode:", decodedResult);
      },
    );
  }

  #submitAndRestart() {
    this.#hideBarInstructions();
    this.#hideQrInstructions();

    this.#submitForm();
    this.scan();
  }

  #showBarInstructions() {
    if (this.hasBarInstructionsTarget) {
      this.barInstructionsTarget.classList.add("active");
    }
  }

  #hideBarInstructions() {
    if (this.hasBarInstructionsTarget) {
      this.barInstructionsTarget.classList.remove("active");
    }
  }

  #showQrInstructions() {
    if (this.hasQrInstructionsTarget) {
      this.qrInstructionsTarget.classList.add("active");
    }
  }

  #hideQrInstructions() {
    if (this.hasQrInstructionsTarget) {
      this.qrInstructionsTarget.classList.remove("active");
    }
  }

  #setManualUrlParam() {
    if (this.hasManualBtnTarget && this.hasCrateNumberTarget) {
      const url = new URL(this.manualBtnTarget.href);
      url.searchParams.set(
        this.crateNumberTarget.name,
        this.crateNumberTarget.value,
      );

      this.manualBtnTarget.href = url.toString();
    }
  }

  #setSuccess(message) {
    if (this.hasFeedbackTarget) {
      this.feedbackTarget.classList.remove("d-none");

      const wrapper = this.feedbackTarget.querySelector(".feedback");
      if (wrapper) {
        wrapper.classList.remove("error");
        wrapper.classList.add("success");
      }

      const icon = this.feedbackTarget.querySelector(".icon");
      if (icon) {
        icon.innerHTML = '<i class="fa fa-check"></i>';
      }
    }

    if (this.hasMessageTarget) {
      this.messageTarget.textContent = message;
    }
  }
}
